import React, { useEffect, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Plus, Edit2, Trash2, Search, Loader2, Key, X } from 'lucide-react';
import api from '@/utils/api';
import { showSuccess, showError } from '@/utils/toast';
import { useAuth } from '@/context/AuthContext';

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'sales_executive',
    mobile_number: '',
    employee_id: '',
    username: '',
    branch_id: '',
    parent_id: '',
  });

  const [branches, setBranches] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => {
    fetchUsers();
    fetchSupportingData();
  }, []);

  const fetchSupportingData = async () => {
    try {
      const [branchesRes, usersRes] = await Promise.all([
        api.get('/branches').catch(() => ({ data: [] })),
        api.get('/users').catch(() => ({ data: [] })),
      ]);
      setBranches(branchesRes.data);
      setAllUsers(usersRes.data);
    } catch (e) { console.warn(e?.message); }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      showError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '',
        full_name: user.full_name,
        role: user.role,
        mobile_number: user.mobile_number,
        employee_id: user.employee_id || '',
        username: user.username || '',
        branch_id: user.branch_id || '',
        parent_id: user.parent_id || '',
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '', password: '', full_name: '', role: 'sales_executive', mobile_number: '',
        employee_id: '', username: '', branch_id: '', parent_id: '',
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingUser) {
        const { password, email, ...updateData } = formData;
        // Remove empty fields to avoid clearing them
        Object.keys(updateData).forEach((k) => updateData[k] === '' && delete updateData[k]);
        await api.put(`/users/${editingUser.id}`, updateData);
        showSuccess('User updated successfully');
      } else {
        const submitData = { ...formData };
        Object.keys(submitData).forEach((k) => submitData[k] === '' && delete submitData[k]);
        await api.post('/auth/register', submitData);
        showSuccess('User created successfully');
      }
      setShowModal(false);
      fetchUsers();
      fetchSupportingData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        showError(detail.map((d) => d.msg).join(', '));
      } else {
        showError(detail || 'Operation failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.delete(`/users/${userId}`);
      showSuccess('User deleted successfully');
      fetchUsers();
    } catch (error) {
      showError('Failed to delete user');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await api.put(`/users/${user.id}`, { is_active: !user.is_active });
      showSuccess(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchUsers();
    } catch (error) {
      showError('Failed to update user status');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.put(`/users/${selectedUserId}/password`, {
        new_password: newPassword,
      });
      showSuccess('Password updated successfully');
      setShowPasswordModal(false);
      setNewPassword('');
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      u.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const roleLabels = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    country_head: 'Country Head',
    zonal_manager: 'Zonal Manager',
    regional_manager: 'Regional Manager',
    area_manager: 'Area Manager',
    asm: 'Area Sales Manager (ASM)',
    branch_manager: 'Branch Manager',
    team_leader: 'Team Leader',
    telecaller: 'Telecaller',
    sales_executive: 'Sales Executive',
    dsa_partner: 'DSA Partner',
    operations_executive: 'Operations Executive',
    credit_manager: 'Credit Manager',
  };

  const isAdmin = currentUser?.role === 'super_admin' || currentUser?.role === 'admin';

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-primary" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Users</h1>
            <p className="text-muted-foreground">Manage system users and their roles</p>
          </div>
          {isAdmin && (
            <button
              data-testid="add-user-button"
              onClick={() => handleOpenModal()}
              className="mt-4 md:mt-0 flex items-center gap-2 px-4 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg transition-colors"
            >
              <Plus size={20} />
              Add User
            </button>
          )}
        </div>

        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
            <input
              type="text"
              data-testid="search-users"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
            />
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted border-b border-border">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Name</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Mobile</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Role</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-muted-foreground">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-accent/50 transition-colors">
                      <td className="px-6 py-4">
                        <p className="font-medium text-foreground">{u.full_name}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground">{u.email}</td>
                      <td className="px-6 py-4 text-sm text-foreground">{u.mobile_number}</td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          {roleLabels[u.role] || u.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => isAdmin && handleToggleActive(u)}
                          disabled={!isAdmin}
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            u.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          } ${isAdmin ? 'cursor-pointer hover:opacity-80' : ''}`}
                        >
                          {u.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {isAdmin && (
                            <>
                              <button
                                data-testid={`edit-user-${u.id}`}
                                onClick={() => handleOpenModal(u)}
                                className="p-2 rounded-lg hover:bg-muted transition-colors"
                                title="Edit"
                              >
                                <Edit2 size={18} className="text-muted-foreground" />
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedUserId(u.id);
                                  setShowPasswordModal(true);
                                }}
                                className="p-2 rounded-lg hover:bg-muted transition-colors"
                                title="Change Password"
                              >
                                <Key size={18} className="text-muted-foreground" />
                              </button>
                              <button
                                data-testid={`delete-user-${u.id}`}
                                onClick={() => handleDelete(u.id)}
                                className="p-2 rounded-lg hover:bg-destructive/10 transition-colors"
                                title="Delete"
                              >
                                <Trash2 size={18} className="text-destructive" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-foreground">{editingUser ? 'Edit User' : 'Add New User'}</h2>
                <button onClick={() => setShowModal(false)} className="p-1 hover:bg-muted rounded">
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Full Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    disabled={!!editingUser}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none disabled:opacity-50"
                  />
                </div>
                {!editingUser && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Password *</label>
                    <input
                      type="password"
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Mobile Number *</label>
                  <input
                    type="tel"
                    required
                    value={formData.mobile_number}
                    onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Role *</label>
                  <select
                    required
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
                  >
                    <option value="super_admin">Super Admin</option>
                    <option value="admin">Admin</option>
                    <option value="country_head">Country Head</option>
                    <option value="zonal_manager">Zonal Manager</option>
                    <option value="regional_manager">Regional Manager</option>
                    <option value="area_manager">Area Manager</option>
                    <option value="asm">Area Sales Manager (ASM)</option>
                    <option value="branch_manager">Branch Manager</option>
                    <option value="team_leader">Team Leader</option>
                    <option value="telecaller">Telecaller</option>
                    <option value="sales_executive">Sales Executive</option>
                    <option value="dsa_partner">DSA Partner</option>
                    <option value="operations_executive">Operations Executive</option>
                    <option value="credit_manager">Credit Manager</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Employee ID</label>
                  <input
                    type="text"
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    placeholder="e.g., GP-EMP-001"
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Username</label>
                  <input
                    type="text"
                    disabled={!!editingUser}
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="e.g., john.doe"
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Branch</label>
                  <select
                    value={formData.branch_id}
                    onChange={(e) => setFormData({ ...formData, branch_id: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
                  >
                    <option value="">-- No Branch --</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>{b.name} ({b.code})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Reporting Manager</label>
                  <select
                    value={formData.parent_id}
                    onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
                  >
                    <option value="">-- Auto (Current Admin) --</option>
                    {allUsers.filter((u) => ['super_admin', 'admin', 'country_head', 'zonal_manager', 'regional_manager', 'area_manager', 'asm', 'branch_manager', 'team_leader'].includes(u.role)).map((u) => (
                      <option key={u.id} value={u.id}>{u.full_name} ({roleLabels[u.role] || u.role})</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-3 border border-border rounded-lg hover:bg-accent">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="submit-user"
                    className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submitting && <Loader2 className="animate-spin" size={18} />}
                    {editingUser ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showPasswordModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-foreground">Change Password</h2>
                <button onClick={() => setShowPasswordModal(false)} className="p-1 hover:bg-muted rounded">
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">New Password *</label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowPasswordModal(false)} className="flex-1 px-4 py-3 border border-border rounded-lg hover:bg-accent">
                    Cancel
                  </button>
                  <button type="submit" disabled={submitting} className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2">
                    {submitting && <Loader2 className="animate-spin" size={18} />}
                    Update Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Users;
